// Module 16(b) §8 — the create use case, and the home of the ported `complete()` payload builder.
//
// `buildPayload` is the load-bearing unit of this module: it IS the `/export-service` create
// contract, reverse-engineered from `DataExportCreate.vue:505-680`. It is PURE and framework-free
// (no store, no Vue, no i18n, no http) and reads the draft where legacy read 30 sibling refs.
//
// What legacy's `complete()` did that does NOT live here, per the ticket:
//   • the two remaining-limit guards (`:506-512`) → `execute()` below, as a `limitReached` outcome
//   • the axios call + its swal/403 handling (`:656-679`) → `execute()` + the caller's toast
//
// KEY ORDER IS PART OF THE CONTRACT. The golden fixtures compare serialised bodies, so the object
// literal below is built in legacy's exact insertion order, and the conditional keys are appended
// in legacy's order too (platform_details → time_info → project_id/dataset_id → concatenate).
import type {
  ExportJobDraft,
  ExportJobPayload,
  ExportJobPayloadBase,
  ExportPayloadFilter,
  ExportPayloadPlatform,
  ExportPayloadPlatformDetail,
  ExportPlatform,
} from '../models/exportJob.types'
import { exportJobService, type ExportJobService } from '../service/exportJobService'
import { useDataExportStore, type DataExportStore } from '../stores/dataExportStore'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

/**
 * The legacy partition map (`DataExportCreate.vue:591-641`) — an eight-branch `if/else if` chain
 * that produced one `{partition_name, platform_identifier, selected_account_id}` entry per chosen
 * platform. Note that three names are NOT `<platform>_account`: `google_analytics` partitions as
 * `ga4_analytics_property` and `google_search_console` as a bare `search_console`.
 *
 * PLATFORM SHAPE QUIRK (analysis §7.1, settled for this ticket). The wire shape is an ARRAY and is
 * emitted as one here, exactly as legacy emits it — but the legacy Source step could only ever put
 * ONE platform in it: `handlePlatformSelection` (`:392-398`) REPLACES the array with `[item]` on
 * every pick rather than appending. So production has only ever sent single-element arrays, and no
 * multi-platform body was ever observed. Whether the wizard keeps that single-select behaviour is
 * a UI decision for the Source-step ticket; `buildPayload` maps whatever the draft holds and does
 * not enforce a cardinality the backend never published.
 */
const PARTITION_NAMES: Record<ExportPlatform, string> = {
  meta_ads: 'meta_ads_account',
  google_ads: 'google_ads_account',
  tiktok_ads: 'tiktok_ads_account',
  google_analytics: 'ga4_analytics_property',
  appsflyer: 'appsflyer_account',
  adjust: 'adjust_account',
  google_search_console: 'search_console',
  criteo: 'criteo_account',
}

/** Context `buildPayload` cannot derive from the draft. Injected so the builder stays pure. */
export interface CreateExportJobContext {
  /**
   * The primary account email legacy appended to the recipient list for the `email` destination
   * (`:652-654`). Legacy pushed it unconditionally, so an account without one sent a literal
   * `undefined` entry — here an absent email simply appends nothing.
   */
  accountEmail?: string
}

export type CreateExportJobOutcome =
  | { status: 'created' }
  | { status: 'limitReached' }
  | { status: 'missingScope' }
  | { status: 'failed'; error: ApiError }

export interface CreateExportJobFlowDeps {
  store?: DataExportStore
  service?: ExportJobService
}

function platformDetails(draft: ExportJobDraft): Record<string, ExportPayloadPlatformDetail> {
  const details: Record<string, ExportPayloadPlatformDetail> = {}
  // Legacy iterates the PLATFORMS and rebuilds the same metric-derived maps for each one
  // (`:550-575`) — the maps are not platform-scoped. Preserved: with the single-select Source step
  // this always produced exactly one entry, and changing it would change the wire shape.
  for (const platform of draft.platforms) {
    const cohortPeriod: Record<string, string> = {}
    const eventSlug: Record<string, string | string[]> = {}
    for (const metric of draft.columns.filter((column) => column.numeric)) {
      if (metric.selected_cohort_period)
        cohortPeriod[metric.identifier] = metric.selected_cohort_period
      if (metric.selected_event_slug) eventSlug[metric.identifier] = metric.selected_event_slug
    }
    if (Object.keys(cohortPeriod).length === 0 && Object.keys(eventSlug).length === 0) continue
    const detail: ExportPayloadPlatformDetail = {}
    if (Object.keys(cohortPeriod).length > 0) detail.cohort_period = cohortPeriod
    if (Object.keys(eventSlug).length > 0) detail.event_slug = eventSlug
    details[platform] = detail
  }
  return details
}

/** `:591-644` — one entry per chosen platform, then the empty-account entries are dropped. */
function platforms(draft: ExportJobDraft): ExportPayloadPlatform[] {
  return draft.platforms
    .map((platform) => ({
      partition_name: PARTITION_NAMES[platform],
      platform_identifier: platform,
      selected_account_id: (draft.selectedAccounts[platform] ?? []).map((account) => account.id),
    }))
    .filter((entry) => entry.selected_account_id.length > 0)
}

/** `:526-535` — only rows carrying a value ship, and `operator`/`match_mode` are the same key. */
function filters(draft: ExportJobDraft): ExportPayloadFilter[] {
  return draft.filters
    .filter((filter) => Boolean(filter.value))
    .map((filter) => ({
      value: filter.value,
      operator: filter.match_mode.field,
      match_mode: filter.match_mode.field,
      numeric: Boolean(filter.column.numeric),
      field: filter.column.field,
      header: filter.column.header,
    }))
}

export class CreateExportJobFlow {
  private readonly store: DataExportStore
  private readonly service: ExportJobService

  constructor(deps: CreateExportJobFlowDeps = {}) {
    this.store = deps.store ?? useDataExportStore()
    this.service = deps.service ?? exportJobService
  }

  /** The ported `complete()` mapping. Pure: same draft in, same body out, no ambient reads. */
  static buildPayload(
    draft: ExportJobDraft,
    context: CreateExportJobContext = {},
  ): ExportJobPayload {
    const destination = draft.destination.type
    // Built without `concatenate`, which legacy assigns LAST (`:651`); the spread at the bottom
    // appends it in that position, so the serialised key order matches the legacy body exactly.
    const payload: ExportJobPayloadBase = {
      name: draft.name,
      description: draft.description,
      // `+refreshInterval` (`:517`) — the legacy ref held the range's fetch-day count as a string.
      data_fetch_days: Number(draft.dateRange.data_fetch_days) || 0,
      dimensions: draft.columns
        .filter((column) => !column.numeric)
        .map((column) => ({ alias: column.identifier })),
      metrics: draft.columns
        .filter((column) => column.numeric)
        .map((column) => ({ alias: column.identifier })),
      breakdowns: draft.breakdowns.map((breakdown) => ({ alias: breakdown.identifier })),
      date_range: {
        start_date: draft.dateRange.firstDate.replaceAll('/', '-'),
        end_date: draft.dateRange.lastDate.replaceAll('/', '-'),
      },
      destination,
      filters: filters(draft),
      platforms: platforms(draft),
      email: [],
      export_now: true,
      frequency: draft.schedule.frequency,
      update_mode: draft.destination.update_mode,
      selected_template: draft.template?.identifier,
      // `fetch_sort` is always an empty object on the wire (`:542-545`); only `export_sort` is
      // ever populated, and only when a sort metric was chosen (`:585-590`).
      sort_config: {
        fetch_sort: {},
        export_sort: draft.sort.field
          ? { field: draft.sort.field, order: draft.sort.order || null }
          : {},
      },
    }

    const details = platformDetails(draft)
    if (Object.keys(details).length > 0) payload.platform_details = details

    if (draft.schedule.frequency !== 'once' && draft.schedule.time_info) {
      payload.time_info = draft.schedule.time_info
    }

    if (destination === 'big_query') {
      payload.project_id = draft.destination.project_id
      payload.dataset_id = draft.destination.dataset_id
    }

    if (destination === 'email') {
      payload.email = context.accountEmail
        ? [...draft.destination.emails, context.accountEmail]
        : [...draft.destination.emails]
    }

    return { ...payload, concatenate: destination !== 'google_sheets' }
  }

  /**
   * Guard the remaining limit, build, post. Returns an OUTCOME — it never toasts and never opens
   * the upgrade modal itself, so both the pre-flight guard (`:506-512`) and the server-side `403`
   * (`:666-667`) surface as the same `limitReached` signal for the caller to render (contract §12:
   * a create 403 is the upgrade modal, never an error toast).
   */
  async execute(): Promise<CreateExportJobOutcome> {
    const draft = this.store.draft
    if (!draft) return { status: 'missingScope' }

    const oneTime = draft.schedule.frequency === 'once'
    if (oneTime ? !this.store.canCreateOneTime : !this.store.canCreateScheduled) {
      return { status: 'limitReached' }
    }

    const { accountUserId, accountEmail } = useAccount()
    const { workspaceId } = useWorkspace()
    if (accountUserId.value === undefined || workspaceId.value === undefined) {
      return { status: 'missingScope' }
    }

    const context: CreateExportJobContext =
      accountEmail.value === undefined ? {} : { accountEmail: accountEmail.value }
    this.store.setCreating(true)
    try {
      await this.service.create(
        accountUserId.value,
        workspaceId.value,
        CreateExportJobFlow.buildPayload(draft, context),
      )
      this.store.resetDraft()
      return { status: 'created' }
    } catch (cause) {
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      // The draft is deliberately PRESERVED on failure so the wizard can retry (contract §14).
      return error.status === 403 ? { status: 'limitReached' } : { status: 'failed', error }
    } finally {
      this.store.setCreating(false)
    }
  }
}
